import {
  Banner,
  Button,
  Dialog,
  DialogButtons,
  Modal,
  Select,
  Spinner,
  TextInput,
  Wizard
} from '@st/theme'

import { Link } from '@features/link'
import { formatFolderEntityName } from '@features/st-folder-viewer/st-folder-display'
import { supportedReturnTypes, TaxYear } from '@st/tax-folder'
import { match } from '@st/pattern'
import {
  selCanAddClient,
  selRelatedFolders,
  type FolderCreateOneByOneMessage,
  type FolderCreateOneByOneState
} from './folder-create-one-by-one-module'

type Props = {
  onClose: () => void
  onBack: () => void
  state: FolderCreateOneByOneState
  send: (message: FolderCreateOneByOneMessage) => void
}

export function FolderCreateOneByOnePage({ onClose, onBack, state, send }: Props) {
  const isBusinessReturn = state.form.returnType != '1040'
  const formDisabled = state.status == 'running'
  const submission = match(state.status)
    .with('ready', () => ({
      label: 'Generate binder',
      disabled: !selCanAddClient(state),
      message: { type: 'addClient', confirmOnRelatedFolders: true } as const
    }))
    .with('running', () => ({
      label: 'Generating binder...',
      disabled: true,
      message: { type: 'addClient', confirmOnRelatedFolders: true } as const
    }))
    .with('failed', () => ({
      label: 'Try again',
      disabled: false,
      message: { type: 'retryFolderCreation' } as const
    }))
    .exhaustive()

  const taxYears = [2026, 2025, 2024, 2023, 2022, 2021, 2020] as TaxYear[]
  const returnTypes = supportedReturnTypes(state.form.year)

  return (
    <Wizard.Scaffold
      onClose={onClose}
      breadcrumb="Generate binders"
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" disabled={formDisabled} onClick={onBack}>
            Back
          </Button>

          <Button
            disabled={submission.disabled}
            variant="primary"
            aria-busy={state.status === 'running'}
            leadingIcon={
              state.status === 'running' ? (
                <Spinner className="size-4 fill-white text-blue-200" />
              ) : undefined
            }
            onClick={() => send(submission.message)}
          >
            {submission.label}
          </Button>
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main
        title="Generate binders one by one"
        subtitle="Only use this option to generate a binder if the client is not in your prior year tax software."
      >
        <div className="flex w-[600px] flex-col items-stretch gap-4">
          {match(state.lastResponse)
            .with({ status: 'created' }, ({ folder }) => (
              <Banner
                variant="success"
                role="status"
                aria-live="polite"
                aria-label={`Successfully added client ${formatFolderEntityName(folder.entities)}`}
              >
                Successfully added client {formatFolderEntityName(folder.entities)}
              </Banner>
            ))
            .with({ status: 'needs_confirmation' }, ({ relatedFolders }) => (
              <Banner
                variant="warning"
                role="alert"
                aria-live="assertive"
                aria-label="Client already exists. Please confirm you would like to add them."
              >
                Client already exists. Please confirm you would like to add them.
              </Banner>
            ))
            .otherwise(() => null)}

          {state.status == 'failed' && (
            <Banner variant="error" role="alert">
              We couldn&apos;t generate this binder. Try again.
            </Banner>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-base">Tax Year</label>
            <Select
              ariaLabel="Tax Year"
              disabled={formDisabled}
              options={taxYears}
              buildLabel={(v) => String(v)}
              buildValue={(v) => v}
              value={state.form.year}
              onChange={(v) => {
                if (v == undefined) return
                send({ type: 'setField', field: 'year', value: v })
              }}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-base">Return type</label>
            <Select
              ariaLabel="Return type"
              disabled={formDisabled}
              options={returnTypes}
              buildLabel={(v) => v}
              buildValue={(v) => v}
              value={state.form.returnType}
              onChange={(v) => {
                if (v == undefined) return
                send({ type: 'setField', field: 'returnType', value: v })
              }}
            />
          </div>

          <div className="flex flex-row gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-base">First name</label>
              <TextInput
                className="flex-1"
                ariaLabel="First name"
                disabled={formDisabled}
                placeholder="First name"
                value={state.form.firstName}
                onChange={(v) => send({ type: 'setField', field: 'firstName', value: v })}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-base">Last name</label>
              <TextInput
                className="flex-1"
                ariaLabel="Last name"
                disabled={formDisabled}
                placeholder="Last name"
                value={state.form.lastName}
                onChange={(v) => send({ type: 'setField', field: 'lastName', value: v })}
              />
            </div>
          </div>

          {isBusinessReturn && (
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-base">Entity name</label>
              <TextInput
                className="flex-1"
                ariaLabel="Entity name"
                disabled={formDisabled}
                placeholder="Entity name"
                value={state.form.corporationName}
                onChange={(v) =>
                  send({
                    type: 'setField',
                    field: 'corporationName',
                    value: v
                  })
                }
              />
            </div>
          )}

          <div className="flex flex-row gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-base">Email</label>
              <TextInput
                className="flex-1"
                ariaLabel="Email"
                disabled={formDisabled}
                placeholder="Email"
                value={state.form.email}
                onChange={(v) => send({ type: 'setField', field: 'email', value: v })}
              />
            </div>
          </div>
        </div>

        {match(state)
          .with({ confirmDialogVisible: true }, () => (
            <FolderCreateOneByOnePageConfirmation
              state={state}
              send={send}
              onClose={() => send({ type: 'closeConfirmDialog' })}
            />
          ))
          .otherwise(() => null)}
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function FolderCreateOneByOnePageConfirmation({
  onClose,
  state,
  send
}: {
  onClose: () => void
  state: FolderCreateOneByOneState
  send: (message: FolderCreateOneByOneMessage) => void
}) {
  const relatedFolders = selRelatedFolders(state)
  const submission = match(state.status)
    .with('ready', () => ({
      label: 'Add client',
      disabled: false,
      message: { type: 'addClient', confirmOnRelatedFolders: false } as const
    }))
    .with('running', () => ({
      label: 'Adding...',
      disabled: true,
      message: { type: 'addClient', confirmOnRelatedFolders: false } as const
    }))
    .with('failed', () => ({
      label: 'Try again',
      disabled: false,
      message: { type: 'retryFolderCreation' } as const
    }))
    .exhaustive()

  return (
    <Modal isOpen={true}>
      <Dialog
        title="Duplicate client"
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={submission.disabled}
              onClick={() => send(submission.message)}
            >
              {submission.label}
            </Button>
          </DialogButtons>
        }
      >
        {state.status == 'failed' && (
          <Banner variant="error" role="alert">
            We couldn&apos;t generate this binder. Try again.
          </Banner>
        )}
        A client with the same email address already exists:
        <div className="flex flex-col items-start gap-2">
          {relatedFolders.map((f) => (
            <Link
              key={f.id}
              className="text-blue-500 underline hover:text-blue-700"
              target="_blank"
              href={{ name: 'organization_folder', folderId: f.id }}
            >
              {formatFolderEntityName(f.entities)}
            </Link>
          ))}
        </div>
      </Dialog>
    </Modal>
  )
}
